import * as z from "zod";

// *** NOTE: importing from @shared/utils is not working ***
// *** so we are importing with relative path the functions here ***
// It create a circular dependency
import { getAsNumber } from "../../../utils/get-as-number";
import { isEmpty } from "../../../utils/is-empty";
import { LenderEnum, amountSchema } from "../../generic";

import { maturityDateSchema, pastDateSchema } from "./date.validation";

const yesNoSchema = z.enum(["YES", "NO"], {
  error: () => "form:error.required",
});

const mortgageInsuranceSchema = z
  .object({
    insuranceQuestionOriginallyInsured: yesNoSchema,
    insuranceQuestionRefinanceOrRenewal: yesNoSchema,
    insuranceQuestionAddFundsOrIncreasedMortgageAmount: yesNoSchema
      .optional()
      .nullable(),
    insuranceQuestionIncreasedAmortization: yesNoSchema.optional().nullable(),
    maturityDate: maturityDateSchema,
    lastClosingDate: pastDateSchema.optional().nullable(),
    currentAmortization: z.preprocess(
      (v) => (isEmpty(v) ? null : getAsNumber(v)),
      z.number({
        error: () => "form:error.required",
      })
    ),
  })
  .refine(
    (schema) => {
      const isRefinance = schema.insuranceQuestionRefinanceOrRenewal === "YES";
      const addFunds =
        schema.insuranceQuestionAddFundsOrIncreasedMortgageAmount;

      // make sure addFunds is filled when isRefinance is YES
      if (isRefinance && isEmpty(addFunds)) {
        return false;
      }
      return true;
    },
    {
      message: "form:error.required",
      path: ["insuranceQuestionAddFundsOrIncreasedMortgageAmount"],
    }
  )
  .refine(
    (schema) => {
      const isRefinance = schema.insuranceQuestionRefinanceOrRenewal === "YES";
      const increasedAmortization =
        schema.insuranceQuestionIncreasedAmortization;

      // make sure increasedAmortization is filled when isRefinance is YES
      if (isRefinance && isEmpty(increasedAmortization)) {
        return false;
      }
      return true;
    },
    {
      message: "form:error.required",
      path: ["insuranceQuestionIncreasedAmortization"],
    }
  )
  .refine(
    (schema) => {
      const addFunds =
        schema.insuranceQuestionAddFundsOrIncreasedMortgageAmount;
      const increasedAmortization =
        schema.insuranceQuestionIncreasedAmortization;

      // make sure lastClosingDate is filled when addFunds or increasedAmortization is filled
      if (
        (addFunds === "YES" && isEmpty(schema.lastClosingDate)) ||
        (increasedAmortization === "YES" && isEmpty(schema.lastClosingDate))
      ) {
        return false;
      }
      return true;
    },
    {
      message: "form:error.required",
      path: ["lastClosingDate"],
    }
  )
  .refine(
    (schema) => {
      const isInsured = schema.insuranceQuestionOriginallyInsured === "YES";

      if (schema.currentAmortization > 25 && isInsured) {
        return false;
      }
      return true;
    },
    {
      message: "renewing-property:currentAmortization.error",
      path: ["currentAmortization"],
    }
  );

export const renewingMortgageSchema = z
  .object({
    balance: amountSchema,
    interestRate: z
      .number({
        error: () => "form:error.required",
        coerce: false,
      })
      .max(99.99, { message: "form:error.maxInterest" })
      .refine(
        (value) => {
          return value !== null && value > 0;
        },
        {
          message: "form:error.required",
        }
      ),
    interestRateType: z.enum(
      ["FIXED", "VARIABLE", "ADJUSTABLE", "CAPPED_VARIABLE"],
      {
        error: () => "form:error.required",
      }
    ),
    lender: z.enum(LenderEnum, {
      error: () => "form:error.required",
    }),
    mortgageType: z.enum(["STANDARD", "HELOC"], {
      error: () => "form:error.required",
    }),
    payment: z.object({
      amount: z.preprocess(
        (v) => (isEmpty(v) ? null : getAsNumber(v)),
        z
          .number({
            error: () => "form:error.missingAmount",
            coerce: false,
          })
          .max(9_999_999, { message: "form:error.maxDollarAmount" })
          .min(1, { message: "form:error.missingAmount" })
      ),
      frequency: z.enum(
        [
          "WEEKLY",
          "ACCELERATED_WEEKLY",
          "BIWEEKLY",
          "ACCELERATED_BIWEEKLY",
          "SEMIMONTHLY",
          "MONTHLY",
        ],
        {
          error: () => "form:error.missingFrequency",
        }
      ),
    }),
    termType: z.enum(["OPEN", "CLOSED", "CONVERTABLE"], {
      error: () => "form:error.required",
    }),
  })
  .and(mortgageInsuranceSchema)
  .array()
  .optional()
  .nullable();
